import * as React from 'react';
import { cn } from '@/lib/utils';

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const PopoverContext = React.createContext<PopoverContextValue>({
  open: false,
  setOpen: () => {},
});

export interface PopoverProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export function Popover({ open, onOpenChange, children }: PopoverProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const controlled = open !== undefined;
  const currentOpen = controlled ? open : internalOpen;

  function handleSetOpen(v: boolean) {
    if (!controlled) setInternalOpen(v);
    onOpenChange?.(v);
  }

  return (
    <PopoverContext.Provider value={{ open: currentOpen, setOpen: handleSetOpen }}>
      <div className="relative inline-block">{children}</div>
    </PopoverContext.Provider>
  );
}

export function PopoverTrigger({
  asChild: _asChild,
  children,
}: {
  asChild?: boolean;
  children: React.ReactNode;
}) {
  const { open, setOpen } = React.useContext(PopoverContext);
  return (
    <div onClick={() => setOpen(!open)} className="inline-flex cursor-pointer">
      {children}
    </div>
  );
}

export function PopoverContent({
  className,
  align = 'center',
  children,
}: {
  className?: string;
  align?: 'start' | 'center' | 'end';
  children: React.ReactNode;
}) {
  const { open, setOpen } = React.useContext(PopoverContext);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  if (!open) return null;

  const alignClass =
    align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2';

  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-full z-50 mt-2 min-w-[200px] rounded-lg border border-ndd-700 bg-ndd-950 p-4 shadow-lg',
        alignClass,
        className
      )}
    >
      {children}
    </div>
  );
}
